import React from 'react';
import {Button} from 'semantic-ui-react';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {Alert} from 'componentLibrary';
import _ from 'lodash';
import {VendorDetails} from './VendorDetails';
import produce from 'immer';
import {connect} from 'react-redux';
import moment from 'moment-timezone';

const infoAlert = (
  linkedVendorId,
  duns,
  dunsToUEIToggle,
  ueiDateFormat,
  ueiEft,
  vendorSystem
) => {
  let alertContent, alertHeader;
  if (dunsToUEIToggle) {
    alertContent = `The application is for the organization with Unique Entity Identifier (UEI)-Electronic Funds Transfer (EFT) indicator:${ueiEft}.\
    The table below shows the details from SAM.gov and ${vendorSystem} for this organization. \
    FEMA GO last updated with sam.gov on ${ueiDateFormat} UTC.`;
    alertHeader = 'Vendor information for application';
  } else if (duns && linkedVendorId) {
    alertContent = `The application is for the organization with DUNS: ${duns}. \
    The table below shows the details from SAM.gov and ${vendorSystem} for this organization. \
    If there is a discrepancy, this may be because of outdated SAM.gov information, \
    outdated ${vendorSystem} information, recipient error in organization selection, or a combination of all three. \
    If information has been updated in SAM.gov or ${vendorSystem}, it will automatically be updated in this table when you reload the page.`;
    alertHeader = 'The application has been linked to a vendor';
  } else {
    alertContent = `The information does not match between SAM.gov and ${vendorSystem}. Please validate if either of them is the correct information, \
    then contact the user or OCFO to get the incorrect data to be changed. If information has been updated in SAM.gov or ${vendorSystem}, \
    it will automatically be updated in this table when you reload the page. If there are multiple ${vendorSystem} records associated with this DUNS, \
    you must select a single record before linking the application to a vendor.`;
    alertHeader = 'FEMA GO has detected a discrepancy';
  }
  return <Alert type="info" content={alertContent} header={alertHeader} />;
};

const vendorSelectAlert = (
  numberOfVendors,
  vendorSelected,
  linkedVendorId,
  dunsToUEIToggle,
  vendorSystem
) => {
  const multipleText = `There are multiple vendor records in ${vendorSystem} associated with this DUNS number. Please select the correct vendor record to be associated with this grant.`;
  const notFoundText = `There are no vendor records in ${vendorSystem} for this DUNS number. Please contact OCFO to resolve.`;
  const notFoundTextUei = `There is no vendor record in ${vendorSystem} for this organization based on their UEI-EFT. Please contact OCFO to resolve.`;

  const selectedVendorHeader = 'Vendor selected, but not yet linked';
  const selectedVendorText = `You have selected the ${vendorSystem} vendor record with vendor ID ${vendorSelected}. This vendor record will not be associated with this grant until you click on the "Link application to vendor" button.`;
  if (numberOfVendors !== 1 && !vendorSelected) {
    return (
      <Alert
        type={numberOfVendors ? 'warning' : 'error'}
        header={
          numberOfVendors
            ? multipleText
            : dunsToUEIToggle
            ? notFoundTextUei
            : notFoundText
        }
      />
    );
  }
  if (numberOfVendors !== 1 && vendorSelected && !linkedVendorId) {
    return (
      <Alert type="warning" header={selectedVendorHeader} content={selectedVendorText} />
    );
  }
};

export class LinkApplicationToVendor extends React.Component {
  state = {
    showVendorList: false,
    selectedVendorId: null,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const {vendors: propsVendors, linkedVendorId} = nextProps;
    const vendors =
      _.size(propsVendors) > 0 && propsVendors.vendorMatched !== false
        ? produce(propsVendors, (draft) => {
            _.unset(draft, 'loading');
          })
        : [];
    const numVendorsFound = _.size(vendors);
    prevState.vendors = vendors;
    prevState.numVendorsFound = numVendorsFound;

    if (numVendorsFound == 1) {
      prevState.selectedVendorId = _.get(vendors, '0.vendorId');
    } else if (linkedVendorId && prevState.selectedVendorId === null) {
      prevState.selectedVendorId = linkedVendorId;
    }
    return prevState;
  }

  selectVendor = (vendorId) => {
    this.setState({
      showVendorList: false,
      selectedVendorId: vendorId,
    });
  };

  render() {
    const {selectedVendorId, numVendorsFound, vendors} = this.state;
    const {organization, linkedVendorId, saveEntityData, toggles} = this.props;
    const fullDuns =
      _.get(organization, 'duns') + '' + _.get(organization, 'dunsPlus4', '');
    const dunsToUEIToggle = _.includes(toggles, 'dunsToUEI');
    const fimsIntegrationToggle = _.includes(toggles, 'fimsIntegration');
    const vendorSystem = fimsIntegrationToggle ? 'FIMS' : 'IFMIS';

    // Remove GMT as it confuses the moment formatting
    const ueiDate = _.get(organization, 'samLastUpdateDate')
      ? _.get(organization, 'samLastUpdateDate').replace(' GMT', '')
      : '--';
    const ueiDateFormat = moment(ueiDate).format('MM/DD/YYYY hh:mm:ss');
    const ueiEft =
      _.get(organization, 'uei') +
      (!_.chain(organization).get('eft').trim().isEmpty().value()
        ? '-' + _.chain(organization).get('eft').trim().value()
        : '');
    return (
      <React.Fragment>
        {infoAlert(
          linkedVendorId,
          fullDuns,
          dunsToUEIToggle,
          ueiDateFormat,
          ueiEft,
          vendorSystem
        )}
        {vendorSelectAlert(
          numVendorsFound,
          selectedVendorId,
          linkedVendorId,
          dunsToUEIToggle,
          vendorSystem
        )}
        {numVendorsFound > 0 || !_.isEmpty(organization) ? (
          <VendorDetails
            vendors={vendors}
            organization={organization}
            linkedVendorId={linkedVendorId}
            selectVendor={this.selectVendor}
            dunsToUEIToggle={dunsToUEIToggle}
            fimsIntegrationToggle={fimsIntegrationToggle}
          />
        ) : (
          <h2>Not found</h2>
        )}
        {!dunsToUEIToggle && (
          <div className="text-align-right margin-top--double margin-bottom">
            <Button
              disabled={!selectedVendorId}
              primary
              onClick={() =>
                saveEntityData(
                  'context.awardRecommendation.payload.vendorId',
                  'awardRecommendation',
                  selectedVendorId
                )
              }
              content="Link application to vendor"
            />
          </div>
        )}
      </React.Fragment>
    );
  }
}

export const mapStateToProps = (state) => ({
  toggles: state.toggles.toggles,
});

export default connect(mapStateToProps)(connectDynamic(LinkApplicationToVendor));
